import { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  BookOpen,
  Search,
  TrendingUp,
  Star,
  ThumbsUp,
  ThumbsDown,
  Loader2,
  Eye,
} from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { AdminUser } from '@/lib/authService'
import ReactMarkdown from 'react-markdown'

interface Category {
  id: string
  name: string
  slug: string
  description: string
  icon: string
  display_order: number
}

interface Article {
  id: string
  category_id: string
  title: string
  slug: string
  summary: string
  content: string
  views: number
  helpful_count: number
  not_helpful_count: number
  tags: string[]
  category_name?: string
  category_icon?: string
  helpfulness_percentage?: number
}

interface KnowledgeBaseProps {
  user: AdminUser | null
}

export default function KnowledgeBase({ user }: KnowledgeBaseProps) {
  const [loading, setLoading] = useState(true)
  const [categories, setCategories] = useState<Category[]>([])
  const [featuredArticles, setFeaturedArticles] = useState<Article[]>([])
  const [popularArticles, setPopularArticles] = useState<Article[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
  const [selectedArticle, setSelectedArticle] = useState<Article | null>(null)
  const [showArticleDialog, setShowArticleDialog] = useState(false)
  const [categoryArticles, setCategoryArticles] = useState<Article[]>([])

  useEffect(() => {
    loadKnowledgeBase()
  }, [])

  useEffect(() => {
    if (selectedCategory) {
      loadCategoryArticles(selectedCategory)
    }
  }, [selectedCategory])

  async function loadKnowledgeBase() {
    try {
      setLoading(true)

      // Load categories
      const { data: cats, error: catsError } = await supabase
        .from('kb_categories')
        .select('*')
        .eq('is_active', true)
        .order('display_order')

      if (catsError) throw catsError

      setCategories(cats || [])

      // Load featured articles
      const { data: featured, error: featuredError } = await supabase
        .from('featured_articles')
        .select('*')
        .limit(3)

      if (featuredError) throw featuredError

      setFeaturedArticles(featured || [])

      // Load popular articles
      const { data: popular, error: popularError } = await supabase
        .from('popular_articles')
        .select('*')
        .limit(5)

      if (popularError) throw popularError

      setPopularArticles(popular || [])
    } catch (error) {
      console.error('Error loading knowledge base:', error)
    } finally {
      setLoading(false)
    }
  }

  async function loadCategoryArticles(categoryId: string) {
    try {
      const { data, error } = await supabase
        .from('kb_articles')
        .select(`
          *,
          kb_categories!inner(name, icon)
        `)
        .eq('category_id', categoryId)
        .eq('is_published', true)
        .order('title')

      if (error) throw error

      const articlesWithCategory = data?.map(article => ({
        ...article,
        category_name: article.kb_categories.name,
        category_icon: article.kb_categories.icon
      }))

      setCategoryArticles(articlesWithCategory || [])
    } catch (error) {
      console.error('Error loading category articles:', error)
    }
  }

  async function handleViewArticle(article: Article) {
    // Increment view count
    await supabase.rpc('increment_article_view', { article_id: article.id })

    setSelectedArticle(article)
    setShowArticleDialog(true)
  }

  async function handleFeedback(articleId: string, wasHelpful: boolean) {
    if (!user) return

    try {
      await supabase.rpc('record_article_feedback', {
        p_article_id: articleId,
        p_user_id: user.id,
        p_was_helpful: wasHelpful,
      })

      alert(wasHelpful ? 'Thanks for your feedback!' : 'Thanks for letting us know. We\'ll improve this article.')

      // Reload to update counts
      loadKnowledgeBase()
      if (selectedCategory) {
        loadCategoryArticles(selectedCategory)
      }
    } catch (error) {
      console.error('Error recording feedback:', error)
    }
  }

  function filterArticles(articles: Article[]) {
    if (!searchTerm) return articles

    return articles.filter(article =>
      article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      article.summary?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      article.tags?.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-3">
          <BookOpen className="h-8 w-8" />
          Knowledge Base
        </h1>
        <p className="text-muted-foreground mt-1">
          Find answers and learn how to use FaithYoke
        </p>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search for help articles..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Featured Articles */}
      {featuredArticles.length > 0 && !searchTerm && (
        <div>
          <div className="flex items-center gap-2 mb-4">
            <Star className="h-5 w-5 text-yellow-500" />
            <h2 className="text-xl font-semibold">Featured Articles</h2>
          </div>
          <div className="grid gap-4 md:grid-cols-3">
            {featuredArticles.map((article) => (
              <Card
                key={article.id}
                className="cursor-pointer hover:shadow-lg transition-shadow"
                onClick={() => handleViewArticle(article)}
              >
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-2xl">{article.category_icon}</span>
                      <Badge variant="outline">{article.category_name}</Badge>
                    </div>
                  </div>
                  <CardTitle className="text-lg mt-2">{article.title}</CardTitle>
                  <CardDescription>{article.summary}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Eye className="h-3 w-3" />
                      {article.views} views
                    </div>
                    <div className="flex items-center gap-1">
                      <ThumbsUp className="h-3 w-3" />
                      {article.helpful_count}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Categories */}
      <Tabs value={selectedCategory || 'all'} onValueChange={(val) => setSelectedCategory(val === 'all' ? null : val)}>
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="all">All</TabsTrigger>
          {categories.map((category) => (
            <TabsTrigger key={category.id} value={category.id}>
              <span className="mr-2">{category.icon}</span>
              {category.name}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="all" className="mt-6">
          <div className="grid gap-6 md:grid-cols-2">
            {/* Popular Articles */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5" />
                  <CardTitle>Popular Articles</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {filterArticles(popularArticles).map((article) => (
                    <div
                      key={article.id}
                      className="p-3 border rounded-lg hover:bg-accent cursor-pointer transition-colors"
                      onClick={() => handleViewArticle(article)}
                    >
                      <div className="flex items-start gap-2">
                        <span>{article.category_icon}</span>
                        <div className="flex-1">
                          <p className="font-medium">{article.title}</p>
                          <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                            <span>{article.views} views</span>
                            <span>
                              {article.helpfulness_percentage}% helpful
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Categories Overview */}
            <Card>
              <CardHeader>
                <CardTitle>Browse by Category</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {categories.map((category) => (
                    <div
                      key={category.id}
                      className="p-4 border rounded-lg hover:bg-accent cursor-pointer transition-colors"
                      onClick={() => setSelectedCategory(category.id)}
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-3xl">{category.icon}</span>
                        <div>
                          <p className="font-semibold">{category.name}</p>
                          <p className="text-sm text-muted-foreground">{category.description}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {categories.map((category) => (
          <TabsContent key={category.id} value={category.id} className="mt-6">
            <div className="grid gap-4 md:grid-cols-2">
              {categoryArticles.map((article) => (
                <Card
                  key={article.id}
                  className="cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => handleViewArticle(article)}
                >
                  <CardHeader>
                    <CardTitle className="text-lg">{article.title}</CardTitle>
                    <CardDescription>{article.summary}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <Eye className="h-3 w-3" />
                        {article.views}
                      </div>
                      <div className="flex items-center gap-1">
                        <ThumbsUp className="h-3 w-3" />
                        {article.helpful_count}
                      </div>
                      {article.tags && article.tags.length > 0 && (
                        <div className="flex gap-1 flex-wrap">
                          {article.tags.slice(0, 2).map((tag) => (
                            <Badge key={tag} variant="secondary" className="text-xs">
                              {tag}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
        ))}
      </Tabs>

      {/* Article Dialog */}
      <Dialog open={showArticleDialog} onOpenChange={setShowArticleDialog}>
        <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
          {selectedArticle && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <span>{selectedArticle.category_icon}</span>
                  {selectedArticle.title}
                </DialogTitle>
                <DialogDescription>
                  <div className="flex items-center gap-4 text-sm">
                    <div className="flex items-center gap-1">
                      <Eye className="h-3 w-3" />
                      {selectedArticle.views} views
                    </div>
                    <div className="flex items-center gap-1">
                      <ThumbsUp className="h-3 w-3" />
                      {selectedArticle.helpful_count} helpful
                    </div>
                  </div>
                </DialogDescription>
              </DialogHeader>

              <div className="prose dark:prose-invert max-w-none">
                <ReactMarkdown>{selectedArticle.content}</ReactMarkdown>
              </div>

              <div className="border-t pt-4 mt-6">
                <p className="text-sm font-medium mb-3">Was this article helpful?</p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => handleFeedback(selectedArticle.id, true)}
                  >
                    <ThumbsUp className="h-4 w-4 mr-2" />
                    Yes, helpful
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => handleFeedback(selectedArticle.id, false)}
                  >
                    <ThumbsDown className="h-4 w-4 mr-2" />
                    Not helpful
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
